import {useAuth} from '@common/auth/use-auth';
import {useTrans} from '@ui/i18n/use-trans';
import {useSettings} from '@ui/settings/use-settings';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {cn} from '@ui/utils/cn';
import {ReactNode} from 'react';
import {Link} from 'react-router';

export const LOGO_FRAME_ASPECT = '516 / 117';

/** Shared Surfy mark: ~30% smaller than the previous header caps (52px / 76px). */
export const brandLogoSizeClassName = 'h-8 max-h-8 md:h-11 md:max-h-11';

const logoFrameClassName =
  'relative block h-full aspect-[516/117] max-h-full bg-transparent';

/** Full wordmark, keep aspect, no left-crop. */
const logoImageClassName =
  'block h-full w-full max-w-full bg-transparent object-contain object-left';

type Props = {
  color?: 'dark' | 'light' | 'auto';
  className?: string;
  logoType?: 'wide' | 'compact' | 'auto';
  url?: string;
};
export function Logo({
  color = 'auto',
  className,
  logoType = 'auto',
  url,
}: Props) {
  const isDarkMode = useIsDarkMode();

  if (color === 'auto') {
    color = isDarkMode ? 'light' : 'dark';
  }

  if (logoType === 'compact') {
    return <CompactLogo color={color} className={className} url={url} />;
  } else if (logoType === 'wide') {
    return <WideLogo color={color} className={className} url={url} />;
  }

  return <AutoLogo color={color} className={className} url={url} />;
}

export function BrandLogoImage({
  src,
  className,
  imgClassName,
  alt = '',
}: {
  src: string;
  className?: string;
  imgClassName?: string;
  alt?: string;
}) {
  return (
    <span className={cn(logoFrameClassName, className)}>
      <img
        src={src}
        alt={alt}
        className={cn(logoImageClassName, imgClassName)}
      />
    </span>
  );
}

function CompactLogo({color, className, url}: Omit<Props, 'logoType'>) {
  const {branding} = useSettings();

  const src =
    color === 'dark' && branding.logo_dark_mobile
      ? branding.logo_dark_mobile
      : branding.logo_light_mobile ||
        (color === 'dark' ? branding.logo_dark : branding.logo_light);

  if (!src) return null;

  return (
    <WrapperLink url={url} className={className}>
      <BrandLogoImage src={src} />
    </WrapperLink>
  );
}

function AutoLogo({color, className, url}: Omit<Props, 'logoType'>) {
  const {branding} = useSettings();

  let wideLogo: string;
  let compactLogo: string;
  if (color === 'light') {
    wideLogo = branding.logo_light;
    compactLogo = branding.logo_light_mobile;
  } else {
    wideLogo = branding.logo_dark;
    compactLogo = branding.logo_dark_mobile;
  }

  if (!wideLogo && !compactLogo) {
    return null;
  }

  const src = wideLogo || compactLogo;

  return (
    <WrapperLink url={url} className={className}>
      <span className={logoFrameClassName}>
        <picture className="block h-full w-full bg-transparent">
          <source srcSet={compactLogo || wideLogo} media="(max-width: 768px)" />
          <source srcSet={wideLogo || compactLogo} media="(min-width: 768px)" />
          <img src={src} alt="" className={logoImageClassName} />
        </picture>
      </span>
    </WrapperLink>
  );
}

function WideLogo({color, className, url}: Omit<Props, 'logoType'>) {
  const {branding} = useSettings();

  const src =
    color === 'dark' && branding.logo_dark
      ? branding.logo_dark
      : branding.logo_light;

  if (!src) return null;

  return (
    <WrapperLink url={url} className={className}>
      <BrandLogoImage src={src} />
    </WrapperLink>
  );
}

type WrapperLinkProps = {
  children: ReactNode;
  url?: string;
  className?: string;
};
function WrapperLink({children, url, className}: WrapperLinkProps) {
  const {trans} = useTrans();
  const {getRedirectUri} = useAuth();

  return (
    <Link
      to={url || getRedirectUri()}
      className={cn('h-full shrink-0 bg-transparent', className)}
      aria-label={trans({message: 'Go to homepage'})}
    >
      {children}
    </Link>
  );
}